import endpoints from '../../constants/endpoints'
import { httpAuth } from '../../config/apiClient'
import Auth from '../../services/authService'
import { setLocale, setThemeConfig } from './general'
import {
  buildStoresByArea,
  encode,
  extractSID,
  getCurrentActiveGroupIdByMaxLevel,
  getGroupsMaxLevel,
  getStoresLength,
  handleError,
} from '../../utils'
import { handleGetPosts, setIsAllPostsCollapsed } from './feed'
import { logoutHandler } from './auth'
import {
  AppTheme,
  IConfigGroup,
  IConfigRetailUser,
  IConfigStateGroups,
  IAudienceScope,
  ITag,
  RootState,
  UserWritePermittedContext,
} from 'constants/interfaces'
import { getTreeNodesIds } from 'components/CommonComponents/GroupsTree/utils'
import { AxiosError, AxiosResponse } from 'axios'
import { setReportsTagsList } from './reports'
import colors from 'constants/colors'
import { isEmpty } from 'lodash'
import keys from 'constants/keys'
import { ThunkAction } from 'redux-thunk'
import { AnyAction } from 'redux'

export const theme: AppTheme = {
  elementsColor: colors.pinkDark,
  backgroundLight: colors.pinkLight,
  backgroundLight2: colors.pinkMedium,
  backgroundMedium: colors.pinkMedium,
}

export const actionTypes = {
  SET_CONFIG: '[CONFIG] SET_CONFIG',
  SET_RETAIL_CONFIG: '[CONFIG] SET_RETAIL_CONFIG',
  SET_STATUS_MENUS: '[CONFIG] SET_STATUS_MENUS',
  SET_TRANSLATIONS: '[CONFIG] SET_TRANSLATIONS',
  SET_ACTIVE_GROUP_ID: '[CONFIG] SET_ACTIVE_GROUP_ID',
  SET_USER_WRITE_PERMITTED_CONTEXT: '[CONFIG] SET_USER_WRITE_PERMITTED_CONTEXT',
  SET_USER_WRITE_TASK_PERMISSIONS: '[CONFIG] SET_USER_WRITE_TASK_PERMISSIONS',
  SET_IS_CONFIG_LOADING: 'SET_IS_CONFIG_LOADING',
  SET_RETAIL_USERS: 'SET_RETAIL_USERS',
  SET_RETAIL_USERS_OBJECT: '[CONFIG] SET_RETAIL_USERS_OBJECT',
  SET_FULL_USER_NAME: '[CONFIG] SET_FULL_USER_NAME',
  SET_ENCODED_UID: '[CONFIG] SET_ENCODED_UID',
  SET_MAPPED_RETAIL_USERS_BY_GROUPS: '[CONFIG] SET_MAPPED_RETAIL_USERS_BY_GROUPS',
  SET_MAPPED_GROUPS_BY_USERS: '[CONFIG] SET_MAPPED_GROUPS_BY_USERS',
  SET_MAPPED_GROUPS_BY_USERS_BY_FEATURE: '[CONFIG] SET_MAPPED_GROUPS_BY_USERS_BY_FEATURE',
  SET_CONFIG_ERROR_STATUS: '[CONFIG] SET_CONFIG_ERROR_STATUS',
  SET_MAPPED_GROUP_IDS_BY_USERS: '[CONFIG] SET_MAPPED_GROUP_IDS_BY_USERS',
  SET_GROUPS: '[CONFIG] SET_GROUPS',
  SET_TAGS: '[CONFIG] SET_TAGS',
  SET_PERMITTED_TREE_CONTEXTS: '[CONFIG] SET_PERMITTED_TREE_CONTEXTS',
  SET_AUDIENCE_SCOPE: '[CONFIG] SET_AUDIENCE_SCOPE',
}

export const setGroups = (groups: IConfigStateGroups) => ({
  type: actionTypes.SET_GROUPS,
  payload: groups,
})

export const setTags = (tags: ITag[]) => ({
  type: actionTypes.SET_TAGS,
  payload: tags,
})

export const setConfigErrorStatus = (status: number | null) => ({
  type: actionTypes.SET_CONFIG_ERROR_STATUS,
  payload: status,
})

export const setEncodedUID = (encodedUID: string) => ({ type: actionTypes.SET_ENCODED_UID, payload: encodedUID })

export const setMappedRetailUsersByGroups = (mappedRetailUsersByGroups: object) => ({
  type: actionTypes.SET_MAPPED_RETAIL_USERS_BY_GROUPS,
  payload: mappedRetailUsersByGroups,
})
export const setMappedGroupsByUsers = (mappedGroupsByUsers: object) => ({
  type: actionTypes.SET_MAPPED_GROUPS_BY_USERS,
  payload: mappedGroupsByUsers,
})
export const setMappedGroupsByUsersByFeature = (mappedGroupsByUsersByFeature: object) => ({
  type: actionTypes.SET_MAPPED_GROUPS_BY_USERS_BY_FEATURE,
  payload: mappedGroupsByUsersByFeature,
})
export const setMappedGroupIdsByUsers = (mappedGroupIdsByUsers: object) => ({
  type: actionTypes.SET_MAPPED_GROUP_IDS_BY_USERS,
  payload: mappedGroupIdsByUsers,
})
export const setRetailUsers = (users: IConfigRetailUser[]) => ({
  type: actionTypes.SET_RETAIL_USERS,
  payload: users,
})
type FullUserName = {
  firstName: string
  lastName?: string
}
export const setFullUserName = ({ firstName, lastName }: FullUserName) => ({
  type: actionTypes.SET_FULL_USER_NAME,
  payload: { firstName, lastName },
})
export const setRetailUsersObject = (users: object) => ({
  type: actionTypes.SET_RETAIL_USERS_OBJECT,
  payload: users,
})
const setIsConfigLoading = (isLoading: boolean) => ({
  type: actionTypes.SET_IS_CONFIG_LOADING,
  payload: isLoading,
})

const setTranslations = (translations: any) => ({
  type: actionTypes.SET_TRANSLATIONS,
  payload: translations,
})

const setRetailConfig = (config: any) => ({
  type: actionTypes.SET_RETAIL_CONFIG,
  payload: config,
})

export const setConfigActiveGroupId = (groupId: string | null) => ({
  type: actionTypes.SET_ACTIVE_GROUP_ID,
  payload: groupId,
})

export const setConfig = (config: any) => ({
  type: actionTypes.SET_CONFIG,
  config,
})

export const setStatusMenus = (menus: any) => ({
  type: actionTypes.SET_STATUS_MENUS,
  menus,
})

export const getConfig = async (activeGroupID: string | null) => {
  const {
    data: {
      user_config,
      status_menus,
      app_translations_file_url,
      retail_config,
      cdn,
      avatars_path,
      force_update,
      appstore_url,
      play_url,
      media_compression,
      profile_img_url,
      tags,
      vapid_public_key,
    },
  } = await httpAuth.get(endpoints.config, { params: { ...(activeGroupID ? { group: activeGroupID } : {}) } })

  const storesByArea = buildStoresByArea(user_config.stores_areas_details)
  user_config.storesSIDToStoreName = user_config.stores_details
  user_config.stores_details = extractSID(user_config)
  user_config.storesByArea = storesByArea
  user_config.storesByAreaToFetch = JSON.parse(JSON.stringify(storesByArea))
  user_config.storesByAreaLength = getStoresLength(storesByArea)
  user_config.cdn = cdn
  user_config.avatars_path = avatars_path
  user_config.force_update = force_update
  user_config.appstore_url = appstore_url
  user_config.play_url = play_url

  if (vapid_public_key) {
    user_config.vapid_public_key = vapid_public_key
  }
  user_config.maxGroupLevel = getGroupsMaxLevel(user_config.groups)

  if (user_config.retail_users) {
    const retailUsersObject = {}
    user_config.retail_users.forEach((user: any) => {
      retailUsersObject[user.uid] = user
    })
    user_config.retailUsersObject = retailUsersObject
  }
  if (retail_config) {
    user_config.is_cover_image_contain = retail_config.is_cover_image_contain
  }
  if (media_compression) {
    user_config.media_compression = media_compression
  }
  if (profile_img_url) {
    user_config.profile_img_url = profile_img_url
  }
  if (user_config.tags) {
    user_config.user_tags = user_config.tags
  }
  if (tags) {
    user_config.tags = tags
  }
  const groups = user_config.groups
  const groupsKeys = Object.keys(user_config.groups)
  let currentActiveGroupID = user_config.default_group || activeGroupID

  if (!currentActiveGroupID || !groupsKeys.includes(currentActiveGroupID)) {
    currentActiveGroupID = getCurrentActiveGroupIdByMaxLevel(groups)
  }

  return { user_config, status_menus, app_translations_file_url, retail_config, activeGroupId: currentActiveGroupID }
}

export const setPermittedTreeContexts = (permittedTreeContexts: CollectResult) => ({
  type: actionTypes.SET_PERMITTED_TREE_CONTEXTS,
  payload: permittedTreeContexts,
})

export const setAudienceScope = (audienceScope: IAudienceScope) => ({
  type: actionTypes.SET_AUDIENCE_SCOPE,
  payload: audienceScope,
})

const getTranslationTimestamp = (translationsURL: string) => {
  return translationsURL.slice(translationsURL.lastIndexOf('_') + 1, translationsURL.lastIndexOf('.'))
}

const checkUpdateTranslations = (translationsURL: string) => {
  const newTimestamp = getTranslationTimestamp(translationsURL)
  const currentTimestamp = localStorage.getItem('translationsTimestamp')

  if (currentTimestamp && newTimestamp) {
    if (+currentTimestamp < +newTimestamp) {
      localStorage.setItem('translationsTimestamp', newTimestamp)
      return true
    } else {
      return false
    }
  }
  return true
}

export const handleInitialTranslations = () => async (dispatch: any) => {
  try {
    const translations = await fetchTranslations(endpoints.initialTranslations)
    dispatch(setTranslations(translations))
  } catch (error) {
    console.warn('[handleInitialTranslations][ERROR]', error)
  }
}

const fetchTranslations = async (translationsURL: string) => {
  const translationsUrlResponse = await fetch(translationsURL, {
    mode: 'cors',
    cache: 'no-store',
  })
  const translationsJSON = await translationsUrlResponse.json()

  return translationsJSON
}

type MapUsersByGroupsArgs = {
  groups: IConfigStateGroups
  retailUsersObject: { [key: string]: IConfigRetailUser }
}
export const mapUsersByGroups = ({ groups, retailUsersObject }: MapUsersByGroupsArgs) => {
  try {
    const result: { [groupId: string]: string[] } = {}

    for (const groupId in groups) {
      const group = groups[groupId]
      const userIds = group?.uids ?? []

      if (userIds.length === 0) {
        result[groupId] = []
        continue
      }

      if (userIds.length === 1) {
        result[groupId] = [userIds[0]]
        continue
      }
      const avatarManagers: string[] = []
      const managers: string[] = []
      const others: string[] = []

      userIds.forEach((uid) => {
        const user = retailUsersObject[uid]
        if (!user) return
        const role = user?.user_group_role ?? keys.USER_GROUP_ROLES.WORKER
        if (role === keys.USER_GROUP_ROLES.AVATAR_MANAGER) {
          avatarManagers.push(uid)
        } else if (role === keys.USER_GROUP_ROLES.MANAGER) {
          managers.push(uid)
        } else {
          others.push(uid)
        }
      })

      avatarManagers.sort((a, b) => {
        const userA = retailUsersObject[a]
        const userB = retailUsersObject[b]

        const nameA = `${userA.first_name}  ${userA?.last_name || ''}`.toLowerCase()
        const nameB = `${userB.first_name} ${userB?.last_name || ''}`.toLowerCase()

        return nameA.localeCompare(nameB)
      })

      managers.sort((a, b) => {
        const userA = retailUsersObject[a]
        const userB = retailUsersObject[b]

        const nameA = `${userA.first_name}  ${userA?.last_name || ''}`.toLowerCase()
        const nameB = `${userB.first_name} ${userB?.last_name || ''}`.toLowerCase()

        return nameA.localeCompare(nameB)
      })

      others.sort((a, b) => {
        const userA = retailUsersObject[a]
        const userB = retailUsersObject[b]

        const nameA = `${userA.first_name}  ${userA?.last_name || ''}`.toLowerCase()
        const nameB = `${userB.first_name} ${userB?.last_name || ''}`.toLowerCase()

        return nameA.localeCompare(nameB)
      })

      result[groupId] = [...avatarManagers, ...managers, ...others]
    }

    return result
  } catch (error) {
    console.log('[mapUsersByGroups][ERROR]', error)
    return {}
  }
}

export const mapGroupsByUsers = (groups: IConfigStateGroups) => {
  const mappedGroupsByUsers = {}
  const mappedGroupsByUsersByFeature = {}
  const mappedGroupIdsByUsers = {}

  for (const groupId in groups) {
    const group = groups[groupId]
    const { name, uids = [], auth_users_by_ft = {} } = group

    for (const uid of uids) {
      if (!mappedGroupsByUsers[uid]) {
        mappedGroupsByUsers[uid] = []
        mappedGroupIdsByUsers[uid] = []
      }
      mappedGroupsByUsers[uid].push(name)
      mappedGroupIdsByUsers[uid].push(groupId)
    }

    for (const feature in auth_users_by_ft) {
      const authorizedUsers = auth_users_by_ft[feature] ?? []

      if (!mappedGroupsByUsersByFeature[feature]) {
        mappedGroupsByUsersByFeature[feature] = {}
      }

      for (const uid of authorizedUsers) {
        if (!mappedGroupsByUsersByFeature[feature][uid]) {
          mappedGroupsByUsersByFeature[feature][uid] = []
        }

        mappedGroupsByUsersByFeature[feature][uid].push(groupId)
      }
    }
  }

  return {
    mappedGroupsByUsers,
    mappedGroupIdsByUsers,
    mappedGroupsByUsersByFeature,
  }
}

type ComputeAudienceScopeArgs = {
  groups: IConfigStateGroups
  defaultGroup: string | null
  mappedRetailUsersByGroups: { [groupId: string]: string[] }
  tags: ITag[]
}

// Generic "self and below" org-tree scope: the current user's default group and everything below it in the
// org tree, plus the users/tags reachable from that subtree. Computed once here (config load) rather than
// per-render in whatever picker/list consumes it, since groups/users/tags only change on config reload.
// Feature-agnostic by design — see skills/audience-scope/SKILL.md for how to consume it in a new feature.
export const computeAudienceScope = ({
  groups,
  defaultGroup,
  mappedRetailUsersByGroups,
  tags,
}: ComputeAudienceScopeArgs): IAudienceScope => {
  if (!defaultGroup || !groups?.[defaultGroup]) {
    return { groupIds: null, userIds: null, tagIds: null }
  }

  const groupIds = getTreeNodesIds(groups, defaultGroup)
  const groupIdsSet = new Set(groupIds)

  const userIdsSet = new Set<string>()
  groupIds.forEach((groupId) => {
    ;(mappedRetailUsersByGroups[groupId] || []).forEach((uid) => userIdsSet.add(uid))
  })

  const tagIds = (tags || [])
    .filter((tag) => !tag.groups?.length || tag.groups.some((groupId) => groupIdsSet.has(groupId)))
    .map((tag) => tag.sk)

  return { groupIds, userIds: Array.from(userIdsSet), tagIds }
}

export const handleConfig = () => async (dispatch: any, getState: () => RootState) => {
  let translationsURL = null
  const { config, auth, feed } = getState()

  try {
    dispatch(setIsConfigLoading(true))
    // config.activeGroupID is shared, unlocked localStorage (persist:config) across every open
    // Storee tab on this origin — a view-as tab can render before it's had a chance to reset this
    // itself (another tab's write can still be sitting there when this fires on mount), and a
    // stale group 500s GET /config?group=<stale-id> for whichever user this tab actually belongs
    // to. A view-as tab's own identity is never trusted from persisted state (see App.tsx) — group
    // gets the same treatment here, at the actual point of use, so it can't lose that race no
    // matter when this runs relative to whatever else is resetting it.
    const groupIdForRequest = Auth.isViewAsSession() ? null : config.activeGroupID
    const { user_config, status_menus, app_translations_file_url, retail_config, activeGroupId } = await getConfig(
      groupIdForRequest
    )
    translationsURL = app_translations_file_url
    dispatch(setLocale(user_config.locale))
    user_config.myUID = auth.uid

    if (!config.activeGroupID || groupIdForRequest === null) {
      dispatch(setConfigActiveGroupId(activeGroupId))
    }
    if (feed.isAllPostCollapsed == null) {
      dispatch(setIsAllPostsCollapsed(retail_config.is_collapse_posts))
    }
    if (user_config.tags && retail_config.reports_tags) {
      const reportTagsKList = user_config.tags.filter((tag: ITag) => retail_config.reports_tags.includes(tag.sk))
      dispatch(setReportsTagsList(reportTagsKList))
    }

    dispatch(setRetailConfig(retail_config))
    dispatch(setConfig(user_config))
    dispatch(setStatusMenus(status_menus))

    // usersByGroups also feeds computeAudienceScope below, which must stay in sync with the active
    // group on every handleConfig() run (incl. handleGroupChange re-entry) — not just when this guard passes.
    let usersByGroups: { [groupId: string]: string[] } = {}
    if (!isEmpty(user_config.retailUsersObject) && !isEmpty(user_config.groups)) {
      usersByGroups = mapUsersByGroups({
        groups: user_config.groups,
        retailUsersObject: user_config.retailUsersObject,
      })

      const { mappedGroupsByUsers, mappedGroupIdsByUsers, mappedGroupsByUsersByFeature } = mapGroupsByUsers(
        user_config.groups
      )
      dispatch(setMappedRetailUsersByGroups(usersByGroups))
      dispatch(setMappedGroupsByUsers(mappedGroupsByUsers))
      dispatch(setMappedGroupsByUsersByFeature(mappedGroupsByUsersByFeature))
      dispatch(setMappedGroupIdsByUsers(mappedGroupIdsByUsers))
      dispatch(
        setPermittedTreeContexts(
          collectOidsAndUidsFromRoots({ map: user_config.groups, rootOids: user_config.user_groups || [] })
        )
      )
    }
    dispatch(
      setAudienceScope(
        computeAudienceScope({
          groups: user_config.groups,
          defaultGroup: user_config.default_group,
          mappedRetailUsersByGroups: usersByGroups,
          tags: user_config.tags,
        })
      )
    )

    if (!isEmpty(retail_config.theme_config)) {
      const newObject = {
        backgroundLight: retail_config.theme_config?.color_light,
        backgroundLight2: retail_config.theme_config?.color_light_2,
        backgroundMedium: retail_config.theme_config?.color_medium,
        elementsColor: retail_config.theme_config?.elements_color,
        logoUrl: retail_config.theme_config?.logo_image_url,
        avatarImgUrl: retail_config.theme_config?.avatar_image_url,
        is_support_us_date_format: retail_config.theme_config?.is_support_us_date_format ?? false,
      }

      dispatch(setThemeConfig(newObject))
    } else {
      dispatch(setThemeConfig(theme))
    }
    if (auth.uid) {
      const encodedUID = encode(auth.uid)
      dispatch(setEncodedUID(encodedUID))
    }
    dispatch(setConfigErrorStatus(null))
  } catch (err) {
    const error: AxiosError | any = err
    if (error?.response?.status) {
      if (error?.response?.status === 401) {
        handleError(error?.response?.status)
      } else dispatch(setConfigErrorStatus(error?.response?.status))
    } else {
      return new Error(error)
    }
  } finally {
    dispatch(setIsConfigLoading(false))
  }

  try {
    if (translationsURL && checkUpdateTranslations(translationsURL)) {
      const translationsJSON = await fetchTranslations(translationsURL)
      localStorage.setItem('translations', JSON.stringify(translationsJSON))
      dispatch(setTranslations(translationsJSON))
      localStorage.setItem('translationsTimestamp', getTranslationTimestamp(translationsURL))
    } else if (localStorage.getItem('translations')) {
      const currentTranslations = JSON.parse(localStorage.getItem('translations')!)
      dispatch(setTranslations(currentTranslations))
    }
  } catch (error) {
    console.log(error)
  }
}

export const handleUserWritePermitted = () => async (dispatch: any) => {
  try {
    const url = endpoints.getUserWritePermittedContext
    const { data } = await httpAuth.get(url)

    dispatch({
      type: actionTypes.SET_USER_WRITE_PERMITTED_CONTEXT,
      payload: data,
    })
  } catch (err) {
    const error: AxiosError | any = err
    if (error?.response?.status) {
      handleError(error.response.status, () => dispatch(logoutHandler()))
    }
    return Promise.reject(error)
  }
}

export const handleGroupChange = (groupId: string, isRunGetPosts?: boolean) => async (dispatch: any) => {
  dispatch(setConfigActiveGroupId(groupId))
  dispatch(handleConfig())
  if (isRunGetPosts) {
    dispatch(handleGetPosts({ isRefresh: true }))
  }
}

export const setTaskPermissions = (data: UserWritePermittedContext) => ({
  type: actionTypes.SET_USER_WRITE_TASK_PERMISSIONS,
  payload: data,
})

export const handleSetTaskPermittedGroup = () => async (dispatch: any) => {
  try {
    const { data } = await httpAuth.get(endpoints.getUserTaskPermissions)
    dispatch(setTaskPermissions(data))
  } catch (err) {
    const error: AxiosError | any = err
    if (error?.response?.status) {
      handleError(error.response.status, () => dispatch(logoutHandler()))
    }
  }
}

interface ICreateTagBody {
  name: string
  tag_color?: string
  tag_type?: number
}

interface IUpdateTagBody extends ICreateTagBody {
  sk: string
  groups: string[]
  users: string[]
}

export const createTag =
  (body: ICreateTagBody): ThunkAction<Promise<ITag>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: { tags, rid },
    } = getState().config

    try {
      const {
        data: { sk },
      } = await httpAuth.post<{ sk: string }>(endpoints.tag, { ...body, rid })

      const newTag = { ...body, sk }

      dispatch(setTags([...tags, newTag]))
      return newTag
    } catch (error) {
      console.error('[createTag] Error:', error)
      throw new Error('Failed to create tag')
    }
  }

export const updateTag =
  (body: IUpdateTagBody): ThunkAction<Promise<ITag>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: { tags, rid },
    } = getState().config

    const { sk } = body
    const prevTag = tags.find((tag: ITag) => tag.sk === sk)

    if (!prevTag) {
      throw new Error('Tag not found')
    }

    try {
      const tagUpdates = getTagUpdates(prevTag, body)
      const updatedTag = await updateTagWithChanges(prevTag, tagUpdates, rid)

      const updatedTags = tags.map((tag: ITag) => (tag.sk === sk ? updatedTag : tag))

      dispatch(setTags(updatedTags))

      return updatedTag
    } catch (error) {
      console.error('Error updating tag:', error)
      throw error
    }
  }

export const deleteTag =
  (tag: ITag): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const {
      config: { tags, rid },
    } = getState().config

    try {
      const payload = {
        rid,
        tag_id: tag.sk,
        name: tag.name,
        groups_to_delete: tag.groups,
        users_to_delete: tag.users,
      }
      await httpAuth.delete(endpoints.tag, { data: payload })

      const updatedTags = tags.filter((t: ITag) => t.sk !== tag.sk)
      dispatch(setTags(updatedTags))

      return true
    } catch (error) {
      console.error('Error deleting tag:', error)
      throw error
    }
  }

interface TagUpdates {
  groups_to_add: string[]
  groups_to_delete: string[]
  users_to_add: string[]
  users_to_delete: string[]
  name: string
  tag_color?: string
  tag_type?: number
}

export const getTagUpdates = (prevTag: ITag, newTag: ITag): TagUpdates => {
  const prevGroups = prevTag.groups || []
  const newGroups = newTag.groups || []
  const prevUsers = prevTag.users || []
  const newUsers = newTag.users || []

  const groups_to_add = newGroups.filter((group) => !prevGroups.includes(group))
  const groups_to_delete = prevGroups.filter((group) => !newGroups.includes(group))
  const users_to_add = newUsers.filter((user) => !prevUsers.includes(user))
  const users_to_delete = prevUsers.filter((user) => !newUsers.includes(user))

  return {
    groups_to_add,
    groups_to_delete,
    users_to_add,
    users_to_delete,
    name: newTag.name,
    tag_color: newTag.tag_color,
    tag_type: newTag.tag_type,
  }
}

export const updateTagWithChanges = async (tag: ITag, updates: TagUpdates, rid: string): Promise<ITag> => {
  const { groups_to_add, groups_to_delete, users_to_add, users_to_delete, name, tag_color, tag_type } = updates

  const requests: Array<Promise<AxiosResponse<void>>> = []

  const hasNameOrColorChanged = name !== tag.name || tag_color !== tag.tag_color || tag_type !== tag.tag_type

  if (groups_to_delete.length || users_to_delete.length) {
    requests.push(
      httpAuth.put<void>(endpoints.tag, {
        rid,
        tag_id: tag.sk,
        groups_to_delete,
        users_to_delete,
        groups_to_add: [],
        users_to_add: [],
        name,
        tag_color,
        tag_type,
      })
    )
  }

  if (groups_to_add.length || users_to_add.length) {
    requests.push(
      httpAuth.put<void>(endpoints.tag, {
        rid,
        tag_id: tag.sk,
        groups_to_add,
        users_to_add,
        groups_to_delete: [],
        users_to_delete: [],
        name,
        tag_color,
        tag_type,
      })
    )
  }

  if (
    hasNameOrColorChanged &&
    !groups_to_add.length &&
    !groups_to_delete.length &&
    !users_to_add.length &&
    !users_to_delete.length
  ) {
    requests.push(
      httpAuth.put<void>(endpoints.tag, {
        rid,
        tag_id: tag.sk,
        groups_to_add: [],
        users_to_add: [],
        groups_to_delete: [],
        users_to_delete: [],
        name,
        tag_color,
        tag_type,
      })
    )
  }

  if (requests.length) {
    await Promise.all(requests)
  }

  const prevGroups = tag.groups || []
  const prevUsers = tag.users || []

  const updatedGroups = [
    ...prevGroups.filter((g) => !groups_to_delete.includes(g)),
    ...groups_to_add.filter((g) => !prevGroups.includes(g)),
  ]

  const updatedUsers = [
    ...prevUsers.filter((u) => !users_to_delete.includes(u)),
    ...users_to_add.filter((u) => !prevUsers.includes(u)),
  ]

  const updatedTag: ITag = {
    ...tag,
    name,
    tag_color,
    tag_type,
    groups: updatedGroups,
    users: updatedUsers,
  }

  return updatedTag
}

export function extractSidsFromGroups(groups: IConfigStateGroups): string[] {
  return Object.values(groups)
    .flatMap((group) => group.sid)
    .filter((sid) => sid && sid !== '')
    .sort()
}

let cachedGroups: IConfigStateGroups | null = null
let cachedSids: string[] = []

export function extractSidsFromGroupsMemoized(groups: IConfigStateGroups): string[] {
  if (cachedGroups === groups) {
    return cachedSids
  }

  cachedGroups = groups
  cachedSids = extractSidsFromGroups(groups)
  return cachedSids
}

export interface CollectOptions {
  isAllowed?: (g: IConfigGroup) => boolean
  uidsAsSet?: boolean
}

export interface CollectResult {
  oids: string[]
  uids: string[] | Set<string>
}

export function collectOidsAndUidsFromRoots({
  map,
  rootOids,
  options,
}: {
  map: IConfigStateGroups
  rootOids: string[]
  options?: CollectOptions
}): CollectResult {
  const { isAllowed = () => true, uidsAsSet = false } = options || {}

  const visited = new Set<string>()
  const parentFirstOrder: string[] = []

  // traverse each root group
  for (const rootOid of rootOids) {
    const stack = [rootOid]

    while (stack.length) {
      const oid = stack.pop()!
      if (visited.has(oid)) continue
      visited.add(oid)
      parentFirstOrder.push(oid)

      const node = map[oid]
      if (!node) continue

      if (Array.isArray(node.child)) {
        for (const childOid of node.child) {
          if (childOid && !visited.has(childOid)) {
            stack.push(childOid)
          }
        }
      }
    }
  }

  // convert to post-order and collect uids
  const oids: string[] = []
  const uidsSet = new Set<string>()

  for (let i = parentFirstOrder.length - 1; i >= 0; i--) {
    const oid = parentFirstOrder[i]
    const node = map[oid]
    if (!node) continue

    oids.push(oid)

    if (isAllowed(node) && Array.isArray(node.uids)) {
      for (const uid of node.uids) {
        if (uid) uidsSet.add(uid)
      }
    }
  }

  return {
    oids,
    uids: uidsAsSet ? uidsSet : Array.from(uidsSet),
  }
}
